import { useState, useEffect } from "react";
import { fetchTransactions,exportTransactionsPdf } from "../services/api";
import { Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1 });
  const [filters, setFilters] = useState({
    search: "",
    type: "",
    status: "",
    from_date: "",
    to_date: "",
  });
  const [notification, setNotification] = useState({ show: false, message: "", type: "success" });
  const [isLoading, setIsLoading] = useState(true);

  const loadTransactions = async (page = 1) => {
    setIsLoading(true);
    try {
      const response = await fetchTransactions({ ...filters, page });
      setTransactions(response.data || []);
      setPagination({
        current_page: response.current_page,
        last_page: response.last_page,
      });
    } catch (error) {
      showNotification("Error loading transactions: " + error.message, "error");
      setTransactions([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const areFiltersEmpty =
      !filters.search &&
      !filters.type &&
      !filters.status &&
      !filters.from_date &&
      !filters.to_date;

    if (areFiltersEmpty) loadTransactions(1);
  }, [filters]);

  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification((prev) => ({ ...prev, show: false })), 2000);
  };

  const handleFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleApplyFilters = () => loadTransactions(1);

  const handleClearFilters = () => {
    setFilters({
      search: "",
      type: "",
      status: "",
      from_date: "",
      to_date: "",
    });
  };

  const getBadgeClass = (type, value) => {
    const map = {
      success: "bg-success text-success",
      failed: "bg-danger text-danger",
      topup: "bg-primary text-primary",
      transfer: "bg-success text-success",
    };
    return `badge ${map[value] || "text-secondary"}`;
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.last_page) loadTransactions(page);
  };

  return (
    <div className="container-fuild">
      <div className="theme-card-white">
          <div className="theme-card-header">
            <h5 className="card-head">Transactions</h5>
          </div>
          {/* ✅ Notification */}
          {notification.show && (
            <div
              className={`position-fixed top-0 end-0 m-3 alert alert-${notification.type === "success" ? "success" : "danger"
                } shadow`}
              role="alert"
              style={{ zIndex: 1050 }}
            >
              {notification.message}
            </div>
          )}

          {/* ✅ Filters */}
          <div className="filte-main">
            <div className="filte-inner">
              <input
                type="text"
                name="search"
                placeholder="Search by name or remarks"
                className="form-control"
                value={filters.search}
                onChange={handleFilterChange}
              />
            </div>

            <div className="filte-inner">
              <select
                name="type"
                value={filters.type}
                onChange={handleFilterChange}
                className="form-select"
              >
                <option value="">All Types</option>
                <option value="topup">Topup</option>
                <option value="transfer">Transfer</option>
              </select>
            </div>

            <div className="filte-inner">
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="form-select"
              >
                <option value="">All Status</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            <div className="filte-inner">
              <input
                type="date"
                name="from_date"
                value={filters.from_date}
                onChange={handleFilterChange}
                className="form-control"
              />
            </div>

            <div className="filte-inner">
              <input
                type="date"
                name="to_date"
                value={filters.to_date}
                onChange={handleFilterChange}
                className="form-control"
              />
            </div>
            <div className="filte-inner-btn">
              <button onClick={handleApplyFilters} className="theme-btn">Apply Filters</button>
              <button onClick={handleClearFilters} className="border-btn">Clear</button>
              <button
                 onClick={() => exportTransactionsPdf(filters)}
                className="border-btn"
              >
                 Export PDF
              </button>
            </div>
          </div>

          {/* ✅ Loading */}
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table theme-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Sender</th>
                    <th>Receiver</th>
                    <th>Amount</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Remarks</th>
                    <th>Note</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.length > 0 ? (
                    transactions.map((tx) => (
                      <tr key={tx.id}>
                        <td>{tx.id}</td>
                        <td>{tx.sender?.name || `User #${tx.sender_id}`}</td>
                        <td>{tx.receiver?.name || `User #${tx.receiver_id}`}</td>
                        <td>${parseFloat(tx.amount).toFixed(2)}</td>
                        <td>
                          <span className={getBadgeClass("type", tx.type)}>{tx.type}</span>
                        </td>
                        <td>
                          <span className={getBadgeClass("status", tx.status)}>{tx.status}</span>
                        </td>
                        <td>{tx.remarks}</td>
                        <td>
                          {tx.note ? (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`tooltip-note-${tx.id}`}>
                                  {tx.note}
                                </Tooltip>
                              }
                            >
                              <span className="text-truncate d-inline-block" style={{ maxWidth: "120px", cursor: "pointer" }}>
                                {tx.note}
                              </span>
                            </OverlayTrigger>
                          ) : (
                            "N/A"
                          )}
                        </td>

                        <td>{new Date(tx.created_at).toLocaleString()}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center py-4 text-muted">
                        No transactions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* ✅ Pagination */}
              <div className="d-flex justify-content-end align-items-center p-3">
                <nav>
                  <ul className="pagination mb-0">
                    {/* Previous Button */}
                    <li className={`page-item ${pagination.current_page === 1 ? "disabled" : ""}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(Math.max(pagination.current_page - 1, 1))}
                      >
                        Prev
                      </button>
                    </li>

                    {/* Page Numbers */}
                    {[...Array(pagination.last_page)].map((_, i) => (
                      <li
                        key={i}
                        className={`page-item ${pagination.current_page === i + 1 ? "active" : ""
                          }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(i + 1)}
                        >
                          {i + 1}
                        </button>
                      </li>
                    ))}

                    {/* Next Button */}
                    <li
                      className={`page-item ${pagination.current_page === pagination.last_page ? "disabled" : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          handlePageChange(Math.min(pagination.current_page + 1, pagination.last_page))
                        }
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>

            </div>
          )}
      </div>
    </div>
  );
};

export default Transactions;
